import { Component, ElementRef, EventEmitter, OnInit, Output, QueryList, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { WorksheetService } from '../../worksheet/services/worksheet.service';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { PrecastWorksheet, PreCastWorksheetPatchDto, PreCastWorkSheetTestResultPatchDto, PreCastWorkSheetTestResultsDto } from '../../worksheet/models/precast-worksheet.modal';
import { DateTimeUtils } from 'src/app/shared/util/date-time.utils';
import { FormService } from 'src/app/shared/services/form.service';
import { WorksheetResponse } from '../../worksheet/models/worksheet-response.modal';
import { SharedService } from '../../service/shared.service';
import { INotes } from '../../models/inotes.modal';
import { MatDialog } from '@angular/material/dialog';
import { NotesComponent } from 'src/app/shared/components/notes/notes.component';
import { InstructionsComponent } from 'src/app/shared/components/instructions/instructions.component';
import { ProjectDetailsComponent } from 'src/app/shared/components/project-details/project-details.component';
import { MatTableDataSource } from '@angular/material/table';
import { AssignToLabtechComponent } from 'src/app/shared/components/assign-to-labtech/assign-to-labtech.component';
import { UserService } from 'src/app/shared/services/user.service';
import { SelectionModel } from '@angular/cdk/collections';
import { ExportTableService } from '../../service/export-table.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PrecastService } from 'src/app/shared/services/precast.service';
import { IDropdown } from 'src/app/shared/models/i-dropdown-type.modal';
import { CanComponentDeactivate } from 'src/app/shared/guard/unsaved-changes';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { PrecastTechWorksheetPdfService } from '../../worksheet/components/precast-table/precast-tech-worksheet-pdf.service';
import { PrecastTechWorksheetExcelService } from '../../worksheet/components/precast-table/precast-tech-worksheet-excel.service';
import { messagesConfig } from 'src/app/shared/constants/message.constants';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
    selector: 'app-precast-worksheet',
    templateUrl: './precast-worksheet.component.html',
    styleUrls: ['./precast-worksheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PrecastWorksheetComponent implements OnInit, CanComponentDeactivate  {
  @Output() assigned = new EventEmitter<boolean>();
  @Output() rowDeletionSuccess = new EventEmitter<void>();
  @ViewChildren('weightInput') weightInputs!: QueryList<ElementRef>;
  @ViewChildren('trInput') trInputs!: QueryList<ElementRef>;
  @ViewChildren('cellInput') cellInputs!: QueryList<ElementRef>;
  selection = new SelectionModel<PrecastWorksheet>(true,[]);
  
  hasUnsavedChanges = false;
  projectId: string | null = null;
  projectStatuses: IDropdown[];
  private readonly destroyed$ = new Subject<boolean>();
  public dataSource = new MatTableDataSource<PrecastWorksheet>();
  public worksheets: PrecastWorksheet[];
  public worksheetRecords: WorksheetResponse | null = null;
  private originalWorksheetData: PrecastWorksheet[] = [];
  public note: INotes | undefined;
  userRoles: string[];
  daysLeft: number = 0;
  noteText: string | undefined = '';
  headerData: any;
  rowData: any;
  columns = [
    { key: 'select', displayName: '' },
    { key: 'inHouseId', displayName: 'In-House Id' },
    { key: 'sampleId', displayName: 'Sample Id' },
    { key: 'castDate', displayName: 'Cast Date' },
    { key: 'sampleType', displayName: 'Sample Type' },
    { key: 'mixDesign', displayName: 'Mix Design' },
    { key: 'tare', displayName: 'Tare' },
    { key: 'mouldWeight', displayName: 'Mould Weight' },
    { key: 'sampleDiameter', displayName: 'Sample Diameter' },
    { key: 'sampleHeight', displayName: 'Sample Height' },
    { key: 'dryWeight', displayName: 'Dry Weight' },
    { key: 'loss', displayName: 'Loss' },
    { key: 'volume', displayName: 'Volume' },
    { key: 'testNo', displayName: 'Test #' },
    { key: 'weight', displayName: 'Weight' },
    { key: 'tr', displayName: 'TR' },
    { key: 'moisture', displayName: 'Moisture' },
    { key: 'density', displayName: 'Density' },
    { key: 'addTestResult', displayName: 'Add' },
    { key: 'assignedTo', displayName: 'Assigned To' },
    { key: 'status', displayName: 'Status' },
    { key: 'action', displayName: '' },
    { key: 'sort', displayName: 'sort' }
  ];
  displayedColumns = this.columns.map(c => c.key);
  dragDisabled = true;
  
  constructor(
    private router: Router, 
    private route: ActivatedRoute,
    private worksheetService: WorksheetService,
    private formService: FormService,
    private sharedService: SharedService,
    public dialog: MatDialog,
    public userService: UserService,
    private exporttableService: ExportTableService,
    private snackBar: MatSnackBar,
    private precastService: PrecastService,
    private precastTechWorksheetPdfService: PrecastTechWorksheetPdfService,
    private precastTechWorksheetExcelService: PrecastTechWorksheetExcelService,
    private dialogService: DialogService
  ) { }
  
  canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasUnsavedChanges) return true;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'Pre-Cast-Worksheet',
      data: {
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Do you want to leave without saving?',
        cancelText: 'Stay',
        confirmText: 'Leave'
      },
      disableClose: true,
      enterAnimationDuration: '0ms',
      exitAnimationDuration: '0ms',
    });

    return dialogRef.afterClosed().pipe(
      map(result => result === 'YES')
    );
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.queryParamMap.get('projectId');
        this.precastService.fetchProjectStatuses().subscribe({
      next: (projectStatues) => {
        this.projectStatuses = projectStatues;
      },
    });
    this.fetchWorksheet(this.projectId ?? '');
    this.fetchWorksheetNotes(this.projectId ?? '');
    this.fetchWorksheetDetails(this.projectId ?? '');
    this.userAccountAndRole();
  }

  onDrop(event: CdkDragDrop<any[]>) {
    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;

    if (previousIndex !== currentIndex) {
      moveItemInArray(
        this.dataSource.data,
        previousIndex,
        currentIndex
      );

      this.dataSource.data.forEach((item, index) => {
        item.sortOrder = index + 1;
      });

      this.dataSource.data = [...this.dataSource.data];
      this.hasUnsavedChanges = true;
    }

    this.dragDisabled = true;
  }

  enableDrag(): void {
    this.dragDisabled = false;
  }

  shouldHideSortColumn(): boolean {
    const totalRows = this.dataSource.data.length;

    const parentRowsCount = this.dataSource.data
      .filter(row => row.groupOrder === 0)
      .length;

    return (
      totalRows === 1 ||
      (totalRows > 1 && parentRowsCount === 1) ||
      !this.roleCheck(['Manager', 'Admin'])
    );
  }

  viewSubmissionForm(): void {
    const params = {
      f: 'w',
    };
    const navigationExtras: NavigationExtras = {
      queryParams: params,
      state: {
        isShipmentReceived: this.worksheetRecords?.project?.storageCompartment
      }
    };

    this.formService.updateData(null);
    this.router.navigate([`/review/form/precast/${this.projectId}`], navigationExtras);
  }

  completeWorksheet(): void {
    this.worksheetService.completeWorksheet(this.projectId ?? '').subscribe({
      next: (data) =>  {
        if(data === "Completed") {
          this.snackBar.open('Completed and Assigned to Lab Manager', 'DONE', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
          });
        } else {
          this.snackBar.open('' + data, 'INFORMATION', {
            duration: 3000,
            verticalPosition: 'top',
            panelClass: ['default-snackbar'],
          });
        }
        this.fetchWorksheetDetails(this.projectId ?? '');
        this.fetchWorksheetNotes(this.projectId ?? '');
      },
      error: (err: any) => console.error(err)
    });
  }

  printTargetWeights() {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(
        ['/precast-print'],
        { queryParams: { projectId: this.projectId } }
      )
    );

    window.open(url, '_blank');
  }

  userAccountAndRole() {
    this.userService.userRole?.subscribe({
      next: (roles) => {
        this.userRoles = roles || [];
      }
    });
  }
  
  preventNumberInputChange(event: WheelEvent): void {
    (event.target as HTMLInputElement).blur();
  }

  toggleSelection(row: PrecastWorksheet) {
    this.selection.toggle(row);
  }

  isAllSelected() {
    return this.selection.selected.length === this.dataSource.data.length;
  }

  isIndeterminate() {
    return this.selection.selected.length > 0 && !this.isAllSelected();
  }

  isMultipleSelected(row: any): boolean {
    return this.selection.selected.length >= 1 && this.selection.selected.includes(row);
  }

  formatCastDate(value: any): string {
    return DateTimeUtils.formatDateAndTime(value, 'MM/dd/yyyy');
  }

  downloadTRWorksheet() {
    this.precastTechWorksheetPdfService.generateTRWorksheet(
      this.worksheetRecords,
      this.worksheets,
    );
  }

  downloadWorksheetReport() {
    this.precastTechWorksheetExcelService.generateWorksheetReport(
      this.worksheetRecords,
      this.worksheets,
    );
  }

  roleCheck(roles: string[]): boolean {
    return roles.some((role) => this.userRoles.includes(role));
  }

  onMixDesignClick(element: any): void {
    const params = new URLSearchParams({
      mixDesignCode: element.mixDesignCode,
      mixReference: element.mixReference
    });

    window.open(`/mix-design/search?${params.toString()}`, '_blank');
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  isLastRowOfSample(row: PrecastWorksheet, index: number): boolean {
    const currentSampleId = row.sampleId;
    const nextRow = this.dataSource.data[index + 1];

    if (!nextRow) return false;
    return nextRow.sampleId !== currentSampleId;
  }

  onFocusClearZero(obj: any, field: string) {
    if (obj[field] === 0) {
      obj[field] = null;
    }
  }

  onBlurRestoreZero(obj: any, field: string) {
    if (obj[field] === null || obj[field] === '') {
      obj[field] = 0;
    }
  }

  isTestingCompleted(): boolean {
    if (!this.projectStatuses || !this.worksheetRecords?.project.projectStatusTypeId) {
      return false;
    }

    const testingStatus = this.projectStatuses.find(
      status => status.label === 'Testing In Progress'
    );

    const isCorrectProjectStatus = testingStatus
      ? this.worksheetRecords.project.projectStatusTypeId === testingStatus.id
      : false;

    return isCorrectProjectStatus && this.allRowsValid();
  }

  allRowsValid(): boolean {
    if (!this.dataSource?.data?.length) return false;

    return this.dataSource.data.every(row =>
      this.calculateRowStatus(row)
    );
  }

  calculateRowStatus(row: PrecastWorksheet): boolean {
    const hasRequiredFields =
      Number(row.tare ?? 0) > 0 &&
      Number(row.sampleDiameter ?? 0) > 0 &&
      Number(row.sampleHeight ?? 0) > 0;

    const mouldWeightRequired = row.sampleType === "Grout/Slurry" || row.sampleType === "FTB";
    const hasValidMouldWeight = !mouldWeightRequired || Number(row.mould ?? 0) > 0;
    const testResults = row.preCastWorkSheetTestResults ?? [];

    const allTestsValid =
      testResults.length > 0 &&
      testResults.every(t =>
        Number(t.weight ?? 0) > 0 &&
        Number(t.tr ?? 0) > 0
      );

    row.status = hasRequiredFields && hasValidMouldWeight && allTestsValid;
    return row.status;
  }

  getTestStatusClass(test: any): string {
    const weight = Number(test.weight ?? 0);
    const tr = Number(test.tr ?? 0);

    const hasWeight = weight > 0;
    const hasTr = tr > 0;

    if (hasWeight && hasTr) {
      return 'status-green';
    }
    return 'status-red';
  }

  onCellKeyPress(event: KeyboardEvent, row: any, testIndex: number, field: 'weight' | 'tr'): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const tests = row.preCastWorkSheetTestResults || [];

      // Move to next test in the same row
      const nextTestIndex = testIndex + 1;
      if (nextTestIndex < tests.length) {
        this.focusField(row.id, nextTestIndex, field);
        return;
      }

      // Last test in this row → first test of next row
      const rows = this.dataSource.data;
      const currentRowIndex = rows.findIndex(r => r.id === row.id);

      if (currentRowIndex === -1) return;
      const nextRow = rows[currentRowIndex + 1];
      if (!nextRow?.id) return;
      const nextRowTests = nextRow.preCastWorkSheetTestResults || [];

      if (nextRowTests.length > 0) {
        this.focusField(nextRow.id, 0, field);
      }
      return;
    }

    if (event.key == 'Tab') {
      event.preventDefault();
      const tests = row.preCastWorkSheetTestResults || [];

      const fieldOrder = ['weight', 'tr'];
      const currentFieldIndex = fieldOrder.indexOf(field);

      let nextFieldIndex = currentFieldIndex + 1;
      let nextTestIndex = testIndex;

      // 1: move inside same test (weight -> tr)
      if (nextFieldIndex < fieldOrder.length) {
        this.focusField(row.id, testIndex, fieldOrder[nextFieldIndex]);
        return;
      }

      // 2: move to next test (TR -> next weight)
      nextFieldIndex = 0;
      nextTestIndex++;

      if (nextTestIndex < tests.length) {
        this.focusField(row.id, nextTestIndex, 'weight');
        return;
      }

      // 3: move to next row first weight
      this.focusNextRow(row.id);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      const rows = this.dataSource.data;
      const currentRowIndex = rows.findIndex(r => r.id === row.id);

      if (currentRowIndex === -1) return;

      const nextRow = rows[currentRowIndex + 1];
      if (!nextRow || !nextRow.id) return;

      const nextRowTests = nextRow.preCastWorkSheetTestResults || [];
      if (testIndex >= nextRowTests.length) return;

      this.focusField(nextRow.id, testIndex, 'weight');
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
    }
  }

  focusNextRow(currentRowId: string) {
    const rows = this.dataSource.data;
    const currentIndex = rows.findIndex(r => r.id === currentRowId);
    if (currentIndex === -1) return;

    const nextRow = rows[currentIndex + 1];
    if (!nextRow) return;

    const target = this.cellInputs?.toArray().find(i => {
      const el = i.nativeElement;

      return (
        el.getAttribute('data-row-id') === nextRow.id &&
        el.getAttribute('data-type') === 'tare'
      );
    });

    target?.nativeElement?.focus();
  }

  focusField(rowId: string, testIndex: number, field: string) {
    const inputs = this.cellInputs.toArray();

    const target = inputs.find(i => {
      const el = i.nativeElement;
      return (
        el.getAttribute('data-row-id') === rowId &&
        el.getAttribute('data-test-index') === testIndex.toString() &&
        el.getAttribute('data-type') === field
      );
    });

    target?.nativeElement.focus();
  }

  onSingleFieldKeyPress(event: KeyboardEvent, row: PrecastWorksheet, field: 'tare' | 'mould' | 'sampleDiameter' | 'sampleHeight'): void {
    if(event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      return
    }
    if (event.key !== 'Enter') { return; }
    event.preventDefault();
    event.stopPropagation();

    const rows = this.dataSource.data;
    const currentRowIndex = rows.findIndex(r => r.id === row.id);
    if (currentRowIndex === -1) { return; }

    // Get next row
    const nextRow = rows[currentRowIndex + 1];
    if (!nextRow?.id) { return; }
    this.focusSingleField(nextRow.id, field); 
  }

  focusSingleField(rowId: string, field: 'tare' | 'mould' | 'sampleDiameter' | 'sampleHeight'): void {
    const inputs = this.cellInputs?.toArray();
    const target = inputs?.find(input => {
      const el = input.nativeElement;

      return (
        el.getAttribute('data-row-id') === rowId &&
        el.getAttribute('data-type') === field
      );
    });

    target?.nativeElement?.focus();
  }

  export() {
    const firstName = this.note?.updatedBy?.firstName || '';
    const lastName = this.note?.updatedBy?.lastName || '';
    const createdAt = this.note?.createdAt;
    const formattedTime = createdAt ? DateTimeUtils.getLocalDateTimeFromUTCDate(createdAt, 'MM/dd/yyyy, h:mm:ss a') : '';
    const notesUpdatedBy = `${firstName} ${lastName} ${formattedTime}`;

    // Same columns and order as the worksheet table, minus the selection, add-test, menu and sort columns, assigned to,
    // and loss and test number, which don't apply once rows are sorted by moisture
    this.headerData = this.columns.filter(c => !['select', 'addTestResult', 'action', 'sort', 'loss', 'testNo', 'assignedTo'].includes(c.key));
    this.rowData = this.buildExportRows();

    this.exporttableService.openInNewTab(
      this.headerData,
      this.rowData,
      this.worksheetRecords,
      notesUpdatedBy,
      this.noteText,
      'precast-worksheet',
    );
  }

  // One export row per test result. Within each sample (the rows between the worksheet's thick borders) the rows are
  // sorted by moisture, highest first, so every row repeats its worksheet row's values to stay identifiable.
  // lastRowOfSample marks the sample's last row, where the worksheet draws its thick border
  private buildExportRows(): any[] {
    const groups: any[][] = [];
    let currentGroup: any[] = [];

    this.dataSource.data.forEach((row: any, index: number) => {
      const testResults: any[] = row.preCastWorkSheetTestResults?.length ? row.preCastWorkSheetTestResults : [{}];
      const status = this.calculateRowStatus(row) ? 'Completed' : 'Not completed';

      testResults.forEach((testResult) => {
        currentGroup.push({
          inHouseId: row.inHouseId ?? '',
          sampleId: row.sampleId ?? '',
          castDate: this.formatCastDate(row.castDate) ?? '',
          sampleType: row.sampleType ?? '',
          mixDesign: row.mixDesignCode ?? '',
          tare: row.tare ?? '',
          mouldWeight: row.mould ?? '',
          sampleDiameter: row.sampleDiameter ?? '',
          sampleHeight: row.sampleHeight ?? '',
          dryWeight: row.dryWeight ?? '',
          volume: row.volume ?? '',
          weight: testResult.weight ?? '',
          tr: testResult.tr ?? '',
          moisture: testResult.moisture ?? '',
          density: row.density ?? '',
          status,
          lastRowOfSample: false,
        });
      });

      const endsSample = this.isLastRowOfSample(row, index);
      if (endsSample || index === this.dataSource.data.length - 1) {
        currentGroup.sort((a, b) => Number(b.moisture || 0) - Number(a.moisture || 0));
        currentGroup[currentGroup.length - 1].lastRowOfSample = endsSample;
        groups.push(currentGroup);
        currentGroup = [];
      }
    });

    return groups.flat();
  }

  saveTable(): void {
    const payload: PreCastWorksheetPatchDto[] = [];

    for (const currentRow of this.dataSource.data) {
      const originalRow = this.originalWorksheetData.find( original => original.id === currentRow.id);
      if (!originalRow) { continue; }

      const changes = this.getChangedWorksheetProperties(originalRow, currentRow);
      if (Object.keys(changes).length === 0) { continue; }
      payload.push({id: currentRow.id!, projectId: currentRow.projectId!, ...changes});
    }

    if (payload.length === 0) {
      this.snackBar.open('No changes to save', 'OK', {
        duration: 3000,
        verticalPosition: 'top',
        panelClass: ['default-snackbar'],
      });

      return;
    }

    this.worksheetService.savePrecastWorksheetTable(payload).subscribe({
      next: () => {
        this.hasUnsavedChanges = false;

        this.snackBar.open('Saved successfully', 'DONE', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['default-snackbar'],
        });

        this.ngOnInit();
      },
      error: () => {
        this.dialogService.open(messagesConfig.saveTableError);
      }
    });
  }

  private getChangedWorksheetProperties(
    original: PrecastWorksheet, 
    current: PrecastWorksheet
  ): Partial<PreCastWorksheetPatchDto> {
    const changes: Partial<PreCastWorksheetPatchDto> = {};

    if (Number(original.tare ?? 0) !== Number(current.tare ?? 0)) {
      changes.tare = Number(current.tare ?? 0);
    }

    if (Number(original.mould ?? 0) !== Number(current.mould ?? 0)) {
      changes.mould = Number(current.mould ?? 0);
    }

    if (Number(original.sampleDiameter ?? 0) !== Number(current.sampleDiameter ?? 0)) {
      changes.sampleDiameter = Number(current.sampleDiameter ?? 0);
    }

    if (Number(original.sampleHeight ?? 0) !== Number(current.sampleHeight ?? 0)) {
      changes.sampleHeight = Number(current.sampleHeight ?? 0);
    }

    if (original.status !== current.status) { 
      changes.status = current.status; 
    }

    if (Number(original.sortOrder ?? 0) !== Number(current.sortOrder ?? 0)) {
      changes.sortOrder = Number(current.sortOrder ?? 0);
    }

    const testChanges = this.getChangedTestResults(
      original.preCastWorkSheetTestResults ?? [],
      current.preCastWorkSheetTestResults ?? []
    );

    if (testChanges.length > 0) {
      changes.preCastWorkSheetTestResults = testChanges;
    }

    return changes;
  }

  private getChangedTestResults(
    originalTests: PreCastWorkSheetTestResultsDto[], 
    currentTests: PreCastWorkSheetTestResultsDto[]
  ): PreCastWorkSheetTestResultPatchDto[] {

    const changes: PreCastWorkSheetTestResultPatchDto[] = [];

    for (const current of currentTests) {
      if (!current.id) {
        changes.push({
          id: null,
          testNumber: current.testNumber,
          weight: Number(current.weight ?? 0),
          tr: Number(current.tr ?? 0)
        });

        continue;
      }

      const original = originalTests.find(test => test.id === current.id);
      if (!original) { continue; }

      const testChange: PreCastWorkSheetTestResultPatchDto = { id: current.id };

      if (Number(original.testNumber ?? 0) !== Number(current.testNumber ?? 0)) {
        testChange.testNumber = current.testNumber;
      }

      if (Number(original.weight ?? 0) !== Number(current.weight ?? 0)) {
        testChange.weight = Number(current.weight ?? 0);
      }

      if (Number(original.tr ?? 0) !== Number(current.tr ?? 0)) {
        testChange.tr = Number(current.tr ?? 0);
      }

      if (Object.keys(testChange).length > 1) { changes.push(testChange); }
    }

    // Deleted tests
    for (const original of originalTests) {
      if (!original.id) { continue; }

      const stillExists = currentTests.some(current => current.id === original.id);

      if (!stillExists) {
        changes.push({
          id: original.id,
          deleted: true
        });
      }
    }
    return changes;
  }

  fetchWorksheetNotes(projectId: string): void {
    this.sharedService.fetchWSNotes(projectId);
    this.sharedService.notes$.subscribe((data) => {
      this.note = data;
    });
    this.sharedService.notesText$.subscribe((data) => {
      this.noteText = data;
    });
  }

  fetchWorksheetDetails(projectId: string): void {
    this.formService.fetchFormData(projectId, 'pre-cast').subscribe({
      next: (result) => {
        const project = result?.project;
        const company = result?.company;
        const comments = result?.comments;
        this.worksheetRecords = {
          project,
          company,
          comments
        };
        if(this.worksheetRecords.project.dueDate) {
          const dueDate = new Date(DateTimeUtils.getLocalDateTimeFromUTCDate(this.worksheetRecords.project.dueDate));
          this.daysLeft = this.findDateDifference(new Date(), dueDate);
        }
      },
    });
  }

  private renumberTests(sample: PrecastWorksheet) {
    if (!sample.preCastWorkSheetTestResults) {
      sample.preCastWorkSheetTestResults = [];
      return;
    }

    sample.preCastWorkSheetTestResults.forEach((t, i) => {
      t.testNumber = i + 1;
    });
  }

  addTestResult(sample: PrecastWorksheet) {
    if (!sample.preCastWorkSheetTestResults) {
      sample.preCastWorkSheetTestResults = [];
    }

    const newTest: PreCastWorkSheetTestResultsDto = {
      id: '',
      preCastWorkSheetId: sample.id!,
      testNumber: 0,
      weight: 0,
      tr: 0,
    };

    sample.preCastWorkSheetTestResults.push(newTest);

    this.renumberTests(sample);
    this.updateCalculations(sample);
    this.dataSource.data = [...this.dataSource.data];

    setTimeout(() => {
      const inputs = this.cellInputs?.toArray();
      if (!inputs?.length) return;

      const target = inputs.find(i => {
        const el = i.nativeElement;
        return (
          el.getAttribute('data-row-id') === sample.id &&
          el.getAttribute('data-type') === 'weight' &&
          el.getAttribute('data-test-index') ===
            (sample.preCastWorkSheetTestResults!.length - 1).toString()
        );
      });

      target?.nativeElement?.focus();
    });
  }

  addTestResultToAll(): void {
    const rows = this.dataSource.data;
    if (!rows?.length) { return; }

    rows.forEach((sample: PrecastWorksheet) => {
      if (!sample.preCastWorkSheetTestResults) {
        sample.preCastWorkSheetTestResults = [];
      }

      const newTest: PreCastWorkSheetTestResultsDto = {
        id: '',
        preCastWorkSheetId: sample.id!,
        testNumber: 0,
        weight: 0,
        tr: 0,
      };

      sample.preCastWorkSheetTestResults.push(newTest);

      this.renumberTests(sample);
      this.updateCalculations(sample);
    });

    // Refresh the table
    this.dataSource.data = [...rows];

    // Focus the newly added Weight field in the first row
    setTimeout(() => {
      const firstRow = rows[0];
      if (!firstRow?.id || !firstRow.preCastWorkSheetTestResults?.length) { return; }
      const lastTestIndex =
        firstRow.preCastWorkSheetTestResults.length - 1;

      this.focusField(firstRow.id, lastTestIndex, 'weight');
    });
  }

  removeTestResult(element: PrecastWorksheet, index: number) {
    if (!element.preCastWorkSheetTestResults) return;
    if (element.preCastWorkSheetTestResults.length === 1) {
      const last = element.preCastWorkSheetTestResults[0];

      last.weight = 0;
      last.tr = 0;

      this.updateCalculations(element);
      this.dataSource.data = [...this.dataSource.data];
      return;
    }

    element.preCastWorkSheetTestResults.splice(index, 1);

    this.renumberTests(element);
    this.updateCalculations(element);
    this.dataSource.data = [...this.dataSource.data];
  }

  calculateDryWeight(element: any) {
    if (!element.preCastWorkSheetTestResults || element.preCastWorkSheetTestResults.length === 0) {
      element.dryWeight = 0;
      return;
    }

    const lastTestResult = element.preCastWorkSheetTestResults[element.preCastWorkSheetTestResults.length - 1];
    const totalTare = Number(element.tare ?? 0) + Number(element.mould ?? 0);
    const lastWeight = Number(lastTestResult.weight ?? 0);

    element.dryWeight = this.round2(lastWeight - totalTare);
  }

  calculateLoss(element: any) {
    if (!element.preCastWorkSheetTestResults || element.preCastWorkSheetTestResults.length === 0) {
      return;
    }

    element.preCastWorkSheetTestResults.forEach((testResult: any, index: number) => {
      if (index === 0) {
        testResult.loss = 0;
      } else {
        const previousWeight = Number(element.preCastWorkSheetTestResults[index - 1].weight ?? 0);
        const currentWeight = Number(testResult.weight ?? 0);

        testResult.loss = this.round2(previousWeight - currentWeight);
      }
    });
  }

  calculateVolume(element: any) {
    const diameter = Number(element.sampleDiameter ?? 0);
    const height = Number(element.sampleHeight ?? 0);

    if (!diameter || !height) {
      element.volume = 0;
      return;
    }

    const radius = diameter / 2;

    const volumeMm3 = Math.PI * Math.pow(radius, 2) * height;
    const volumeCm3 = volumeMm3 / 1000;

    element.volume = this.round2(volumeCm3);
  }

  calculateDensity(element: any) {
    const dryMassG = Number(element.dryWeight ?? 0);
    const volumeCm3 = Number(element.volume ?? 0);

    if (!dryMassG || !volumeCm3) {
      element.density = 0;
      return;
    }

    const massLb = dryMassG / 453.592;
    const volumeFt3 = volumeCm3 / 28316.8466;
    const densityPcf = massLb / volumeFt3;
    element.density = Number(densityPcf.toFixed(2));
  }

  calculateMoisture(element: any) {
    const dryWeight = Number(element.dryWeight ?? 0);
    const totalTare = Number(element.tare ?? 0) + Number(element.mould ?? 0);

    if (!dryWeight) return;
    element.preCastWorkSheetTestResults.forEach((p: any) => {
      const wet = Number(p.weight ?? 0) - totalTare;

      if (!wet || wet <= 0) {
        p.moisture = 0;
        return;
      }
      const m = ((wet - dryWeight) / dryWeight) * 100;
      p.moisture = Number(m.toFixed(2));
    });
  }

  updateCalculations(element: any, pageLoad: boolean = false) {
    if(!pageLoad){
      this.hasUnsavedChanges = true;
    }
    this.calculateDryWeight(element);
    this.calculateLoss(element);
    this.calculateVolume(element);
    this.calculateDensity(element);
    this.calculateMoisture(element);
    this.calculateRowStatus(element);
  }

  openViewNotesDialog(): void {
    const messageObject = {
      projectId: this.projectId,
      notes: this.note,
    };
    this.dialog
      .open(NotesComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetNotes(this.projectId ?? '');
      });
  }

  openViewInstructionsDialog(): void {
    if (!this.worksheetRecords?.comments) return;

    this.dialog.open(InstructionsComponent, {
      panelClass: 'app-dialog',
      data: this.worksheetRecords.comments,
      disableClose: true,
      width: '50%',
    });
  }

  openProjectDetailsComponent(title: string, fieldName: string): void {
    if (!this.worksheetRecords?.project) return;
    const project = this.worksheetRecords.project;
    const messageObject = {
      projectId: this.projectId,
      title,
      fieldName,
      ovenDate: project.ovenDate,
      wetTestData: project.wetTestData,
      dryTestData: project.dryTestData,
    };
    this.dialog
      .open(ProjectDetailsComponent, {
        panelClass: 'app-dialog',
        data: messageObject,
        disableClose: true,
        width: '50%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.fetchWorksheetDetails(this.projectId ?? '');
      });
  }

  openAssignToDialog(worksheetId: string): void {
    if (this.hasUnsavedChanges) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'pre-cast-worksheet',
        data: {
          title: 'Unsaved Changes',
          content: 'You have unsaved changes. Do you want to continue assigning without saving your changes?',
          cancelText: 'Cancel',
          confirmText: 'Continue'
        },
        disableClose: true
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'YES') {
          this.openAssignDialogInternal(worksheetId);
        }
      });
      return;
    }

    this.openAssignDialogInternal(worksheetId);
  }

  private openAssignDialogInternal(worksheetId: string): void {
    this.dialog
      .open(AssignToLabtechComponent, {
        data: {
          worksheetId,
          type: 'precast',
          multipleSelected: this.isMultipleSelected(worksheetId),
          worksheetRows: this.selection.selected,
          totalNumOfWorksheets: this.dataSource.data.length,
          role: this.userRoles
        },
        disableClose: true,
        width: '40%',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((assigned) => {
        if (assigned) {
          this.fetchWorksheet(this.projectId ?? '');
          this.rowDeletionSuccess.emit();
          this.assigned.emit(true);
          this.selection.clear();
          this.hasUnsavedChanges = false;
        }
      });
  }

  findDateDifference(startDate: Date, endDate: Date) {
    const timeDifference = Math.abs(endDate.getTime() - startDate.getTime());
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    return Math.trunc(dayDifference);
  }

  round2(value: number): number {
    return Number(value.toFixed(2));
  }

  reDirectUrl() {
    this.router.navigate(['dashboard']);
  }

  fetchWorksheet(projectId: string): void {
    this.worksheetService
      ?.fetchWorksheetbyId(projectId)
      ?.pipe(takeUntil(this.destroyed$))
      ?.subscribe({
        next: (worksheets) => {
          let initial = 0;
          this.worksheets = worksheets;
          this.worksheets.forEach((worksheet) => {
            if (!worksheet.groupOrder) {
              initial += 1;
            }
            worksheet.sequence = initial;
          });

          worksheets.forEach((worksheet:PrecastWorksheet) => {
            this.updateCalculations(worksheet, true);
          });

          worksheets.forEach((worksheet:PrecastWorksheet) => {
            if (worksheet.truckNumber && worksheet.ticketNumber) {
              worksheet.sampleId = `${worksheet.sampleId}, Truck# ${worksheet.truckNumber}, Ticket# ${worksheet.ticketNumber}`;
            } else if (worksheet.ticketNumber) {
              worksheet.sampleId = `${worksheet.sampleId}, Ticket# ${worksheet.ticketNumber}`;
            } else if (worksheet.truckNumber) {
              worksheet.sampleId = `${worksheet.sampleId}, Truck# ${worksheet.truckNumber}`;
            } else {
              worksheet.sampleId = `${worksheet.sampleId}`;
            }
          });

          worksheets.forEach((worksheet:any) => {
            if (worksheet.assignedTo) {
              const updatedByFirstName = worksheet.assignedTo?.firstName;
              const updatedByLastName = worksheet.assignedTo?.lastName;
              worksheet.assignedTo = `${updatedByFirstName} ${updatedByLastName}`;
            }
          });

          const orderedData = worksheets.map(w => {
            let results = w.preCastWorkSheetTestResults ?? [];
            if (results.length === 0) {
              results = [this.createEmptyTestResult(w.id ?? '')];
            }

            return {
              ...w,
              preCastWorkSheetTestResults: results
                .slice()
                .sort((a, b) => (a.testNumber ?? 0) - (b.testNumber ?? 0))
            };
          });
          this.dataSource.data = orderedData;
          this.dataSource.data.forEach(e => this.updateCalculations(e, true));
          this.originalWorksheetData = structuredClone(this.dataSource.data);
          this.rowData = this.dataSource.data;
          const excludedKeys = ['select', 'action', 'status', 'addTestResult', 'castDate'];
          this.headerData = this.columns.filter(c => !excludedKeys.includes(c.key));
        },
      });
  }

  getSampleIdWithTruckandTicket(worksheet: PrecastWorksheet): string {
    const parts: string[] = [];
    if (worksheet.truckNumber) {
      parts.push(`Truck# ${worksheet.truckNumber}`);
    }
    if (worksheet.ticketNumber) {
      parts.push(`Ticket# ${worksheet.ticketNumber}`);
    }
    return parts.length ? `${parts.join(' | ')}` : '';
  }

  createEmptyTestResult(preCastWorkSheetId: string): PreCastWorkSheetTestResultsDto {
    return {
      id: '',
      preCastWorkSheetId: preCastWorkSheetId,
      testNumber: 1,
      weight: 0,
      tr: 0,
      createdAt: null,
      updatedAt: null,
      createdById: null,
      updatedById: null
    };
  }
}
